import {
  createApi,
  fetchBaseQuery,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query/react";
import { Me } from "../types";

type ApiResponse<T = any> = T;

type ApiError = FetchBaseQueryError & { endpointAddress?: string };

interface MutationArgs {
  endpoint: string;
  id?: string | number;
  body?: any;
}

interface UpdateArgs extends MutationArgs {}
interface PatchArgs extends MutationArgs {}
interface RemoveArgs {
  endpoint: string;
  id?: string | number;
}

interface UpdateResponse<T = any> {
  data: T;
  status?: number;
  headers?: Headers;
}

export const apiSlice = createApi({
  reducerPath: "apiSlice",
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_API_URL}/api`,
    headers: { "Content-Type": "application/json charset=utf-8" },
    credentials: "include",
  }),
  endpoints: (builder) => ({
    // GET
    get: builder.query<ApiResponse, string>({
      queryFn: async (endpoint, _queryApi, _options, fetchBaseQuery) => {
        const res = await fetchBaseQuery(endpoint);
        if (res.data) {
          console.log("SUCCESS:", res);
          return { data: res.data };
        } else {
          const error = res.error as ApiError;
          error.endpointAddress = endpoint;
          console.log("ERROR:", error);
          return { error };
        }
      },
    }),
    // POST
    add: builder.mutation<ApiResponse, MutationArgs>({
      query: ({ endpoint, id, body }) => ({
        url: `${endpoint}/${id || ""}`,
        method: "POST",
        body: JSON.stringify(body),
      }),
    }),
    // PUT
    update: builder.mutation<UpdateResponse, UpdateArgs>({
      query: ({ endpoint, id, body }) => ({
        url: `${endpoint}/${id || ""}`,
        method: "PUT",
        body: JSON.stringify(body),
      }),
      transformResponse: (response, meta: FetchBaseQueryMeta) => ({
        data: response,
        status: meta?.response?.status,
        headers: meta?.response?.headers,
      }),
    }),
    // PATCH
    patch: builder.mutation<ApiResponse, PatchArgs>({
      query: ({ endpoint, id, body }) => ({
        url: `${endpoint}/${id || ""}`,
        method: "PATCH",
        body: JSON.stringify(body),
      }),
    }),
    // DELETE
    remove: builder.mutation<ApiResponse, RemoveArgs>({
      query: ({ endpoint, id }) => ({
        url: `${endpoint}/${id || ""}`,
        method: "DELETE",
      }),
    }),

    login: builder.mutation<
      { success: boolean },
      { email: string; password: string }
    >({
      query: (body) => ({
        url: "/auth/login",
        method: "POST",
        body,
      }),
    }),
    logout: builder.mutation<void, void>({
      query: () => ({
        url: "/auth/logout",
        method: "POST",
      }),
    }),
    getMe: builder.query<Me, void>({
      query: () => "/users/me",
    }),
  }),
});

console.log(process.env.REACT_APP_API_URL);

export const {
  useGetQuery,
  useAddMutation,
  useUpdateMutation,
  usePatchMutation,
  useRemoveMutation,
  useLoginMutation,
  useLogoutMutation,
  useGetMeQuery,
} = apiSlice;
